import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { DollarSign, Building, Users, Zap, MapPin, Truck, Gauge, Calendar } from 'lucide-react';

interface AnalyticsOverviewProps {
  dateFilter: string;
  utilityTypeFilter: string;
  billTypeFilter: string;
  locationFilter: string;
}

const monthlyCharges = [
  { month: 'Jan', 2025: 45000, 2024: 42000, 2023: 40000 },
  { month: 'Feb', 2025: 48000, 2024: 44000, 2023: 41000 },
  { month: 'Mar', 2025: 95000, 2024: 88000, 2023: 85000 },
  { month: 'Apr', 2025: 52000, 2024: 50000, 2023: 48000 },
  { month: 'May', 2025: 46000, 2024: 45000, 2023: 43000 },
  { month: 'Jun', 2025: 48000, 2024: 47000, 2023: 45000 },
  { month: 'Jul', 2025: 50000, 2024: 49000, 2023: 47000 },
  { month: 'Aug', 2025: 92000, 2024: 90000, 2023: 88000 },
  { month: 'Sep', 2025: 89000, 2024: 87000, 2023: 85000 },
  { month: 'Oct', 2025: 98000, 2024: 95000, 2023: 92000 },
  { month: 'Nov', 2025: 55000, 2024: 53000, 2023: 51000 },
  { month: 'Dec', 2025: 48000, 2024: 46000, 2023: 44000 },
];

const costBreakdown = [
  { name: 'SEWER', value: 213635.25, color: '#9ca3af' },
  { name: 'WATER', value: 147308.00, color: '#1e40af' },
  { name: 'REFUSE', value: 83021.10, color: '#16a34a' },
  { name: 'ELECTRIC', value: 66372.00, color: '#3b82f6' },
  { name: 'NATURALGAS', value: 40155.31, color: '#84cc16' },
  { name: 'STORMWATER', value: 6926.45, color: '#06b6d4' },
  { name: 'LIGHTING', value: 4105.27, color: '#eab308' },
  { name: 'FIREPROTECTION', value: 1021.15, color: '#f59e0b' },
];

const utilityCharges = [
  { utilityType: 'SEWER', charges: 213635.25, unitCost: 7.368, unit: 'CCF' },
  { utilityType: 'WATER', charges: 147308.00, unitCost: 4.881, unit: 'CCF' },
  { utilityType: 'REFUSE', charges: 83021.10, unitCost: 0.000, unit: 'TON' },
  { utilityType: 'ELECTRIC', charges: 66372.00, unitCost: 0.140, unit: 'KWH' },
  { utilityType: 'NATURALGAS', charges: 40155.31, unitCost: 0.901, unit: 'THERM' },
  { utilityType: 'STORMWATER', charges: 6926.45, unitCost: 0.000, unit: 'SQFEET' },
  { utilityType: 'LIGHTING', charges: 4105.27, unitCost: 0.313, unit: 'KWH' },
  { utilityType: 'FIREPROTECTION', charges: 1021.15, unitCost: 0.000, unit: 'CCF' },
];

const totalCharges = costBreakdown.reduce((sum, item) => sum + item.value, 0);

export function AnalyticsOverview({
  dateFilter,
  utilityTypeFilter,
  billTypeFilter,
  locationFilter,
}: AnalyticsOverviewProps) {
  return (
    <div className="space-y-6">
      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Total Charges</p>
                <p className="text-2xl font-bold">${(totalCharges / 1000).toFixed(0)}K</p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <DollarSign className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Charges</p>
                <p className="text-2xl font-bold">{costBreakdown.length} Utility Types</p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Zap className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Earliest Month</p>
                <p className="text-lg font-bold">January 2025</p>
                <p className="text-xs text-muted-foreground">143 Locations</p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Calendar className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Latest Month</p>
                <p className="text-lg font-bold">December 2025</p>
                <p className="text-xs text-muted-foreground">424 Billing Accounts</p>
              </div>
              <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <Building className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Charges over the Years</CardTitle>
            <CardDescription>Monthly charges comparison</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyCharges}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value: number) => `$${(value / 1000).toFixed(0)}K`} />
                  <Legend />
                  <Line type="monotone" dataKey="2025" stroke="#ec4899" strokeWidth={2} name="2025" />
                  <Line type="monotone" dataKey="2024" stroke="#3b82f6" strokeWidth={2} strokeDasharray="5 5" name="2024" />
                  <Line type="monotone" dataKey="2023" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" name="2023" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Cost Breakdown</CardTitle>
            <CardDescription>Distribution by utility type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={costBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${(percent * 100).toFixed(1)}%`}
                    outerRadius={100}
                    innerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {costBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => `$${(value / 1000).toFixed(2)}K`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Utility Type Charges Table */}
      <Card>
        <CardHeader>
          <CardTitle>Utility Type Charges</CardTitle>
          <CardDescription>Detailed breakdown by utility type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Utility Type</TableHead>
                  <TableHead className="text-right">Charges</TableHead>
                  <TableHead className="text-right">Unit Cost</TableHead>
                  <TableHead>Unit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {utilityCharges.map((row) => (
                  <TableRow key={row.utilityType}>
                    <TableCell>
                      <Badge variant="outline">{row.utilityType}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      ${row.charges.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-right">
                      ${row.unitCost.toFixed(3)}
                    </TableCell>
                    <TableCell>{row.unit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

